import React from "react";

import classNames from "classnames";

import Badge from "ui/Badge";
import { StatusDotIcon } from "ui/Icons";
import { type GridColumn } from "ui/Grid/util";

import SelectableGrid from "../SelectableGrid";

export type ServerStatus = "primary" | "replica" | "no data";

export type SelectableServer = {
  id: string;
  name: string;
  clusterId: string | null;
  status: ServerStatus;
  hasRecentSnapshot: boolean;
};

const statusRank: Record<ServerStatus, number> = {
  primary: 0,
  replica: 1,
  "no data": 2,
};

const searchValue = (server: SelectableServer) =>
  `${server.name} ${server.clusterId ?? ""}`;

const sortValue = (server: SelectableServer, field: keyof SelectableServer) => {
  switch (field) {
    case "status":
      return statusRank[server.status];
    case "clusterId":
      return (server.clusterId ?? "").toLowerCase();
    default:
      return server.name.toLowerCase();
  }
};

const columns: GridColumn<SelectableServer, keyof SelectableServer>[] = [
  {
    field: "name",
    header: "Server",
    width: "minmax(40%,1fr)",
    renderer: ({ rowData }) => (
      <>
        <StatusDotIcon
          status={rowData.hasRecentSnapshot ? "okay" : "no data"}
        />
        <span className="ml-[4px]">{rowData.name}</span>
      </>
    ),
  },
  {
    field: "clusterId",
    header: "Cluster ID",
    width: "minmax(20%,200px)",
    renderer: ({ rowData }) => (
      <span className="text-[#606060]">{rowData.clusterId || "-"}</span>
    ),
  },
  {
    field: "status",
    header: "Status",
    width: "120px",
    renderer: function StatusCell({ rowData }) {
      if (rowData.status === "no data") {
        return <span className="text-[#606060]">-</span>;
      }
      return (
        <Badge
          className={classNames(
            "border font-medium",
            rowData.status === "replica" &&
              "!bg-[#f0f0f0] !text-[#555] !border-[#ddd]",
          )}
        >
          {rowData.status}
        </Badge>
      );
    },
  },
];

type Props = {
  servers: SelectableServer[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  // Servers this group's rules already match. They are members either way, so they
  // are shown checked and locked rather than as something to add a second time.
  ruleMatchedIds?: string[];
  disabled?: boolean;
};

const ServerPicker = ({
  servers,
  selectedIds,
  onChange,
  ruleMatchedIds,
  disabled,
}: Props) => (
  <SelectableGrid
    items={servers}
    columns={columns}
    selectedIds={selectedIds}
    onChange={onChange}
    disabledIds={ruleMatchedIds}
    disabledTitle="Already included by a matching rule"
    disabled={disabled}
    searchValue={searchValue}
    sortValue={sortValue}
    defaultSortBy="name"
    searchPlaceholder="Search servers by name or cluster ID..."
    emptyMessage="No matching servers."
    selectAllLabel="Select all servers"
    itemLabel={(server) => server.name}
  />
);

export default ServerPicker;
